<template>
  <div>
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-12">
          <h6 class="h2 text-white d-inline-block mb-0">Crear Factura</h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb />
          </nav>
        </div>
      </div>
    </base-header>
    <div class="container-fluid mt--6">
      <div class="row justify-content-center">
        <div class="col-md-6 col-sm-12 col-xs-12 card-wrapper">
          <div class="card mb-4">
            <!-- Card header -->
            <div class="card-header">
              <h3 class="mb-0">Agregar una nueva factura</h3>
            </div>
            <!-- Card body -->
            <div class="card-body">
              <!-- Form groups used in grid -->
              <form class="needs-validation" @submit.prevent="save">
                <div class="row my-4">
                  <div class="col-sm-12">
                    <div class="form-group">
                      <el-upload
                        ref="upload"
                        v-validate="modelValidations.invoice"
                        :auto-upload="false"
                        action="https://jsonplaceholder.typicode.com/posts/"
                        class="upload-demo"
                        name="invoice"
                      >
                        <base-button slot="trigger" type="success">Subir archivo</base-button>
                        <div slot="tip" class="el-upload__tip mt-2 pl-1">Solo archivos pdf</div>
                      </el-upload>

                      <div v-for="err in fileErrors" :key="err.msg">
                        <small class="text-danger">{{ err.msg }}</small>
                      </div>
                    </div>
                  </div>
                </div>

                <seller-select v-if="bySeller" v-model="sellerSelected" cy-id="seller-select" />
                <distributor-select v-else v-model="distributorSelected" cy-id="distributor-select" />

                <div v-if="sellerSelected || distributorSelected" class="form-group">
                  <label class="control-label w-100">
                    <span>Seleccione N° de Liquidación</span>
                  </label>
                  <el-select
                    v-model="formData.liquidationId"
                    v-validate="modelValidations.liquidation"
                    cy-id="liquidation-select"
                    autocomplete="off"
                    filterable
                    name="liquidation"
                    placeholder="Seleccione Liquidación"
                  >
                    <el-option
                      v-for="liquidation in liquidations"
                      :key="liquidation.id"
                      :label="liquidation.id"
                      :value="liquidation.id"
                      cy-id="liquidation-options"
                    >
                    </el-option>
                  </el-select>

                  <small v-show="liquidation.invalid" class="text-danger">
                    {{ getError('liquidation') }}
                  </small>
                </div>

                <div class="row">
                  <div class="col-sm-12">
                    <div class="form-group">
                      <label class="control-label"> N° de Factura </label>

                      <el-input
                        v-model="formData.invoiceNumber"
                        cy-id="invoice-number"
                        type="text"
                        name="invoice-number"
                        placeholder="Número de Factura"
                      />

                      <small v-show="invoiceNumber.invalid" class="text-danger">
                        {{ getError('invoiceNumber') }}
                      </small>
                    </div>
                  </div>
                </div>

                <div class="row">
                  <div class="col-sm-12">
                    <div class="form-group">
                      <label class="control-label"> Descripción </label>

                      <el-input
                        v-model="formData.description"
                        :rows="4"
                        cy-id="description"
                        name="description"
                        placeholder="Descripción"
                        type="textarea"
                      >
                      </el-input>

                      <small v-show="description.invalid" class="text-danger">
                        {{ getError('description') }}
                      </small>
                    </div>
                  </div>
                </div>

                <div class="row">
                  <div class="col-md-12 text-right">
                    <base-button cy-id="save" native-type="submit" type="primary">Guardar</base-button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import RouteBreadCrumb from '@/components/Breadcrumb/RouteBreadcrumb';
import SellerSelect from '@/components/ModelSelects/SellerSelect';
import DistributorSelect from '@/components/ModelSelects/DistributorSelect.vue';
import { Option, Select, Upload } from 'element-ui';
import { mapFields } from 'vee-validate';
import swal from 'sweetalert2';

export default {
  name: 'InvoiceForm',

  components: {
    RouteBreadCrumb,
    SellerSelect,
    DistributorSelect,
    [Upload.name]: Upload,
    [Option.name]: Option,
    [Select.name]: Select,
  },

  data() {
    return {
      validated: false,
      fileErrors: [],
      sellers: [],
      distributors: [],
      sellerSelected: null,
      distributorSelected: null,
      bySeller: false,
      liquidations: [],
      formData: {
        description: '',
        invoice: null,
        invoiceNumber: null,
        liquidationId: null,
      },

      modelValidations: {
        description: {
          regex: /^([-a-z0-9_ ])+$/i,
        },

        liquidation: {
          required: true,
        },

        invoiceNumber: {
          required: true,
        },
      },
    };
  },

  computed: {
    ...mapFields(['description', 'liquidation', 'invoiceNumber']),
  },

  watch: {
    sellerSelected: {
      handler() {
        this.fetchLiquidations();
      },
    },

    distributorSelected: {
      handler() {
        this.fetchLiquidations();
      },
    },
  },

  mounted() {
    this.bySeller = this.$route.params.bySeller;
  },

  methods: {
    beforeUpload() {
      return new Promise((resolve, reject) => {
        this.fileErrors = [];

        if (this.$refs.upload.uploadFiles.length > 1) {
          this.fileErrors.push({
            msg: 'Solo puede cargar un archivo!',
          });
          return reject(false);
        }

        if (this.$refs.upload.uploadFiles[0]) {
          const file = this.$refs.upload.uploadFiles[0].raw;

          const isPDF = file.type === 'application/pdf';
          const isLt2M = file.size / 1024 / 1024 < 10;

          if (!isPDF) {
            this.fileErrors.push({
              msg: 'El archivo debe estar en formato PDF!',
            });
            return reject(false);
          }
          if (!isLt2M) {
            this.fileErrors.push({
              msg: 'El archivo excede los 10MB!',
            });
            return reject(false);
          }
          return resolve(true);
        }
        this.fileErrors.push({
          msg: 'El documento es obligatorio',
        });
        return reject(false);
      });
    },

    async firstFormSubmit() {
      try {
        return await Promise.all([this.$validator.validateAll(), this.beforeUpload()]);
      } finally {
        this.validated = true;
      }
    },

    save() {
      this.firstFormSubmit()
        .then((res) => {
          if (res.every((validation) => validation)) {
            this.formData.invoice = this.$refs.upload.uploadFiles.pop().raw;
            this.addInvoice();
          }
        })
        .catch((e) => {});
    },

    getError(name) {
      return this.errors.first(name);
    },

    isValid(name) {
      return this.validated && !this.errors.has(name);
    },

    fetchLiquidations() {
      const liquidationableId = this.bySeller ? this.sellerSelected : this.distributorSelected;
      const liquidationableType = this.bySeller ? 'sellers' : 'distributors';

      const params = {
        'filter[liquidationable_id]': liquidationableId,
        'filter[liquidationable_type]': liquidationableType,
      };

      this.$services
        .resource('liquidations')
        .get(params)
        .then((res) => {
          this.liquidations = res.data.data.liquidations;
        });
    },

    addInvoice() {
      const headers = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };
      this.$services
        .resource('liquidations')
        .addInvoice(this.formData, headers)
        .then(() => {
          swal({
            title: 'Listo!',
            text: 'La factura fue creada.',
            buttonsStyling: false,
            confirmButtonClass: 'btn btn-success',
            type: 'success',
          }).then(() => this.$router.go(-1));
        });
    },
  },
};
</script>

<style scoped>
.el-autocomplete {
  width: 100%;
}
</style>
